/**
 * KeyLocationsGuidedEditor - Multi-step guided editor for Key Locations section
 *
 * Context-aware component that works in both inline edit and wizard contexts.
 * Progressive disclosure: GPX import is optional, meeting point is always required.
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor with GPX Intelligence
 * Story: SCRUM-232 (US-1.20.6-S.1-02: Guided Editor Shell Component)
 *
 * Steps:
 * 1. MEETING_POINT - Always required (search, manual entry, map)
 * 2. GPX_IMPORT - Optional (GPX-enabled categories only)
 * 3. WAYPOINT_REVIEW - Only if GPX imported (edit, add, delete waypoints)
 * 4. CONFIRMATION - Final review before save
 *
 * @component
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import GuidedEditorProgress from './GuidedEditorProgress';
import MeetingPointStep from './MeetingPointStep';
import GpxImportStep from './GpxImportStep';
import WaypointReviewStep from './WaypointReviewStep';
import ConfirmationStep from './ConfirmationStep';
import ConfirmModal from '../../../shared/ConfirmModal';

// Step IDs
const STEPS = {
  MEETING_POINT: 'meeting_point',
  GPX_IMPORT: 'gpx_import',
  WAYPOINT_REVIEW: 'waypoint_review',
  CONFIRMATION: 'confirmation'
};

// GPX-enabled L1 categories
const GPX_ENABLED_CATEGORIES = [39, 40, 41, 42]; // Hiking, _Mountain, Cycling, Water

/**
 * KeyLocationsGuidedEditor
 *
 * Main guided editor component with step navigation and state management.
 */
const KeyLocationsGuidedEditor = ({
  experienceId,
  categoryL1Id,
  initialData = {},
  context = 'inline', // 'inline' or 'wizard'
  onSave,
  onCancel,
  wizardUpdateField
}) => {
  // State — start at CONFIRMATION if pre-populated data exists (SCRUM-832: AC-04)
  const [currentStepId, setCurrentStepId] = useState(() => {
    if (initialData.meeting_point?.latitude && initialData.meeting_point?.longitude) {
      return STEPS.CONFIRMATION;
    }
    return STEPS.MEETING_POINT;
  });
  const [formData, setFormData] = useState({
    meeting_point: initialData.meeting_point || {},
    waypoints: initialData.waypoints || [],
    route_overview: initialData.route_overview || '',
    route_stats: initialData.route_stats || null,
    elevation_data: initialData.elevation_data || null,
    gpx_source: initialData.gpx_source || null,
    gpx_raw_xml: initialData.gpx_raw_xml || null
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [gpxAnalysisResult, setGpxAnalysisResult] = useState(null);
  const [isGpxEnabled, setIsGpxEnabled] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Determine if category supports GPX
  useEffect(() => {
    setIsGpxEnabled(GPX_ENABLED_CATEGORIES.includes(categoryL1Id));
  }, [categoryL1Id]);

  // Build step configuration based on context
  const getSteps = useCallback(() => {
    const steps = [
      { id: STEPS.MEETING_POINT, label: 'Meeting Point', optional: false }
    ];

    // Add GPX import step if category supports it
    if (isGpxEnabled) {
      steps.push({ id: STEPS.GPX_IMPORT, label: 'GPX Import', optional: true });
    }

    // Add waypoint review step if GPX was imported
    if (gpxAnalysisResult) {
      steps.push({ id: STEPS.WAYPOINT_REVIEW, label: 'Review Waypoints', optional: false });
    }

    // Always add confirmation step
    steps.push({ id: STEPS.CONFIRMATION, label: 'Confirm', optional: false });

    return steps;
  }, [isGpxEnabled, gpxAnalysisResult]);

  const steps = getSteps();

  // Get current step number (1-indexed)
  const currentStepNumber = steps.findIndex(s => s.id === currentStepId) + 1;

  // Update form data
  const _updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);

    // Update wizard field if in wizard context
    if (context === 'wizard' && wizardUpdateField) {
      wizardUpdateField('keyLocations', { ...formData, ...updates });
    }
  }, [context, wizardUpdateField, formData]);

  // Navigation handlers
  const goToNextStep = useCallback(() => {
    const currentIndex = steps.findIndex(s => s.id === currentStepId);
    if (currentIndex < steps.length - 1) {
      setCurrentStepId(steps[currentIndex + 1].id);
    }
  }, [steps, currentStepId]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = steps.findIndex(s => s.id === currentStepId);
    if (currentIndex > 0) {
      setCurrentStepId(steps[currentIndex - 1].id);
    }
  }, [steps, currentStepId]);

  const _goToStep = useCallback((stepId) => {
    // Only allow navigation to completed or current steps
    const targetIndex = steps.findIndex(s => s.id === stepId);
    const currentIndex = steps.findIndex(s => s.id === currentStepId);

    if (targetIndex <= currentIndex) {
      setCurrentStepId(stepId);
    }
  }, [steps, currentStepId]);

  // Validation
  const validateCurrentStep = useCallback(() => {
    switch (currentStepId) {
      case STEPS.MEETING_POINT:
        // Meeting point must have coordinates
        return formData.meeting_point?.latitude && formData.meeting_point?.longitude;

      case STEPS.GPX_IMPORT:
        // GPX import is optional, always valid
        return true;

      case STEPS.WAYPOINT_REVIEW:
        // At least meeting point + any waypoints
        return true;

      case STEPS.CONFIRMATION:
        // Must have meeting point at minimum
        return formData.meeting_point?.latitude && formData.meeting_point?.longitude;

      default:
        return false;
    }
  }, [currentStepId, formData]);

  const isCurrentStepValid = validateCurrentStep();

  // Handle GPX analysis result
  const handleGpxAnalysisComplete = useCallback((analysisResult) => {
    setGpxAnalysisResult(analysisResult);

    // Update form data with GPX analysis
    _updateFormData({
      meeting_point: analysisResult.meeting_point || formData.meeting_point,
      waypoints: analysisResult.waypoints || [],
      route_stats: analysisResult.route_stats,
      elevation_data: analysisResult.elevation_data,
      gpx_source: {
        filename: analysisResult.gpxFilename || analysisResult.metadata?.filename,
        uploaded_at: new Date().toISOString()
      },
      // SCRUM-830: Store raw GPX XML for route persistence
      gpx_raw_xml: analysisResult.gpxRawXml || null
    });

    // Auto-advance to waypoint review
    goToNextStep();
  }, [formData.meeting_point, _updateFormData, goToNextStep]);

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      await onSave(formData);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('[KeyLocationsGuidedEditor] Save failed:', error);
      // Error handling in parent component
    }
  }, [formData, onSave]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCancelModal(true);
      return;
    }

    onCancel();
  }, [hasUnsavedChanges, onCancel]);

  // Render current step
  const renderStep = () => {
    switch (currentStepId) {
      case STEPS.MEETING_POINT:
        return (
          <MeetingPointStep
            meetingPoint={formData.meeting_point}
            categoryL1Id={categoryL1Id}
            onChange={(meetingPointData) => _updateFormData({ meeting_point: meetingPointData })}
          />
        );

      case STEPS.GPX_IMPORT:
        return (
          <GpxImportStep
            experienceId={experienceId}
            categoryL1Id={categoryL1Id}
            onAnalysisComplete={handleGpxAnalysisComplete}
            onSkip={goToNextStep}
          />
        );

      case STEPS.WAYPOINT_REVIEW:
        return (
          <WaypointReviewStep
            waypoints={formData.waypoints}
            anomalies={gpxAnalysisResult?.anomalies || []}
            onChange={(waypointsData) => _updateFormData({ waypoints: waypointsData })}
          />
        );

      case STEPS.CONFIRMATION:
        return (
          <ConfirmationStep
            meetingPoint={formData.meeting_point}
            waypoints={formData.waypoints}
            routeStats={formData.route_stats}
            elevationData={formData.elevation_data}
            categoryL1Id={categoryL1Id}
          />
        );

      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <div className="key-locations-guided-editor">
      {/* Progress Indicator */}
      <GuidedEditorProgress steps={steps} currentStep={currentStepNumber} />

      {/* Step Content */}
      <div className="step-content bg-white rounded-lg shadow-sm border border-gray-200 p-6 min-h-[400px]">
        {renderStep()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-6">
        {/* Cancel / Back */}
        <div>
          {currentStepNumber === 1 ? (
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          ) : (
            <button
              type="button"
              onClick={goToPreviousStep}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back
            </button>
          )}
        </div>

        {/* Next / Save */}
        <div>
          {currentStepNumber === steps.length ? (
            <button
              type="button"
              onClick={handleSave}
              disabled={!isCurrentStepValid}
              className="px-6 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Key Locations
            </button>
          ) : (
            <button
              type="button"
              onClick={goToNextStep}
              disabled={!isCurrentStepValid}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          )}
        </div>
      </div>

      {/* Unsaved changes indicator */}
      {hasUnsavedChanges && context === 'inline' && (
        <div className="mt-4 text-sm text-amber-600 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          You have unsaved changes
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <ConfirmModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={() => {
          setShowCancelModal(false);
          onCancel();
        }}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to cancel?"
        confirmText="Yes, Cancel"
        cancelText="Keep Editing"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

KeyLocationsGuidedEditor.propTypes = {
  /**
   * Experience ID (UUID or 'new' for creation)
   */
  experienceId: PropTypes.string.isRequired,

  /**
   * L1 Category ID (determines GPX availability)
   */
  categoryL1Id: PropTypes.number.isRequired,

  /**
   * Initial key locations data
   */
  initialData: PropTypes.shape({
    meeting_point: PropTypes.object,
    waypoints: PropTypes.array,
    route_overview: PropTypes.string,
    route_stats: PropTypes.object,
    elevation_data: PropTypes.object,
    gpx_source: PropTypes.object,
    gpx_raw_xml: PropTypes.string
  }),

  /**
   * Context: 'inline' (section edit) or 'wizard' (creation wizard)
   */
  context: PropTypes.oneOf(['inline', 'wizard']),

  /**
   * Save handler
   */
  onSave: PropTypes.func.isRequired,

  /**
   * Cancel handler
   */
  onCancel: PropTypes.func.isRequired,

  /**
   * Wizard update field handler (only for wizard context)
   */
  wizardUpdateField: PropTypes.func
};

export default KeyLocationsGuidedEditor;
